export type ReleaseNoteCategory = 'New' | 'Improved' | 'Fixed';

export type ReleaseNoteItem = {
  category: ReleaseNoteCategory;
  title: string;
  description: string;
  audience?: string;
  /** When true, this item is shown only to Super Admin users. */
  internal?: boolean;
};

export type ReleaseNote = {
  version: string;
  date: string;
  headline: string;
  summary: string;
  highlights: string[];
  /** Extra highlights shown only to Super Admin users. */
  internalHighlights?: string[];
  items: ReleaseNoteItem[];
  demoUrl?: string;
  demoAspectRatio?: number;
};

export const getVisibleReleaseNotes = (isSuperAdmin: boolean): ReleaseNote[] =>
  releaseNotes
    .map(release => ({
      ...release,
      items: release.items.filter(item => isSuperAdmin || !item.internal),
    }))
    .filter(release => release.items.length > 0);

export const releaseNotes: ReleaseNote[] = [
  {
    version: '4.5.0',
    date: 'Oct 2026',
    headline:
      'Waiting room visibility, Physician Out tracking, and screen updates',
    summary:
      'This release adds clearer patient care phases, additional status color options, a Ready for PreOp timer, Physician Out tracking, and updates to scheduled patient grouping, registration SMS, and OR Case Flow.',
    highlights: [
      'Clearer Waiting Room Board care phases',
      'Physician Out button and KPI',
      'Scheduled patients grouped by physician',
    ],
    items: [
      {
        category: 'Improved',
        title: 'Waiting Room Board Care Phases',
        audience: 'Family Members',
        description:
          'Improved separation between care phases on the Waiting Room Board so caretakers can more easily find their family member when the board is full.',
      },
      {
        category: 'Improved',
        title: 'Operations and Schedule Status Color Options',
        audience: 'All Staff',
        description:
          'Added configuration options to make status color borders flash and to disable status colors independently on the Operations Monitor and Schedule screens.',
      },
      {
        category: 'Improved',
        title: 'Ready for PreOp Timer',
        audience: 'Admitting Team, PreOp Staff',
        description:
          'Added a timer to the Ready for PreOp tag on the Internal Screen, showing how long a patient has been marked ready while in the waiting room.',
      },
      {
        category: 'New',
        title: 'Physician Out Button on the OR iPad',
        audience: 'Administrators, OR Staff',
        description:
          'Added a Physician Out button on the OR iPad to capture when the physician leaves the room, independently of the existing button sequence.',
      },
      {
        category: 'New',
        title: 'Physician Out to Procedure End KPI',
        audience: 'Administrators, Surgeons',
        description:
          'Added a KPI measuring the time from Physician Out to Procedure End, helping OR managers explain why a room may not be ready for turnover when the physician leaves.',
      },
      {
        category: 'New',
        title: 'Scheduled Patients Grouped by Physician',
        audience: 'All Staff',
        description:
          'Added an option in the Internal Screen configuration to group the Scheduled Patients column by physician, with patients sorted by scheduled time within each group.',
      },
      {
        category: 'New',
        title: 'Registration Package SMS Repeat Option',
        audience: 'Admitting Team, Patients',
        description:
          'Added a repeat option for Registration Package SMS messages.',
      },
      {
        category: 'Improved',
        title: 'OR Case Flow: Actual Room Assignment',
        audience: 'Administrators, Managers',
        description:
          'OR Case Flow now shows cases in the operating room where the surgery actually took place, rather than the scheduled room.',
      },
    ],
  },
  {
    version: '4.4.1',
    date: 'Oct 2026',
    headline: 'Patient flow analytics, schedule access, and readiness visibility',
    summary:
      'This release expands analytics and scheduled reporting, gives liaisons more schedule access, and adds caretaker check-in, cancellation sync, and a Ready for PreOp status on the Internal Screen.',
    highlights: ['Daily patient flow analytics', 'Liaison schedule access', 'Ready for PreOp status'],
    items: [
      {
        category: 'New',
        title: 'Analytics Enhancements',
        audience: 'Administrators',
        description:
          'Added six new daily patient flow charts, specialty-specific analytics, CPT-based filtering for PDF reports, and a new classified PDF report option based on predefined CPT codes.',
      },
      {
        category: 'Fixed',
        title: 'Weekly Scheduled Analytics Fix',
        audience: 'Administrators',
        description:
          'Corrected an issue with the selected day of the week for automatically scheduled analytics reports.',
      },
      {
        category: 'Fixed',
        title: 'Monthly Scheduled Analytics Fix',
        audience: 'Administrators',
        description:
          'Improved monthly report scheduling to account for organizational time zones and ensure reports are generated on the correct day.',
      },
      {
        category: 'New',
        title: 'Liaison Schedule Access',
        audience: 'Liaisons',
        description:
          'Liaisons can now filter schedules and select dates without the schedule automatically advancing to the next day when all procedures are completed. Schedule editing remains restricted.',
      },
      {
        category: 'Improved',
        title: 'Updated PACU Exit Delay Reasons',
        audience: 'PACU Nurses',
        description:
          'Updated the available PACU exit delay reasons to include Sedation, Spinal, Quad Weakness, Pain, POUR, Lengthy Discharge, Comorbidities, Staffing, Care Partner, Orthostatic, and Other.',
      },
      {
        category: 'Improved',
        title: 'PreOp/PACU Staff Assignment Exports',
        audience: 'Administrators',
        description:
          'Staff exports now include assigned staff members and their assignment durations, when available.',
      },
      {
        category: 'New',
        title: 'HST Schedule Cancellation Sync',
        audience: 'Schedulers',
        description:
          'When a patient is moved to the Cancellation column in HST, the patient will automatically be removed from the Ospitek schedule.',
      },
      {
        category: 'New',
        title: 'Caretaker Name Capture',
        audience: 'Front Desk Staff',
        description:
          'Added a caretaker name field to the Waiting Room iPad, allowing caretaker information to be captured during patient check-in.',
      },
      {
        category: 'New',
        title: 'Ready for PreOp Status Indicator',
        audience: 'Front Desk Staff, Pre-Op Nurses',
        description:
          'Added a green “Ready for PreOp” tag to patient tiles on the Internal Screen when the front desk marks a patient as ready. The tag appears alongside the existing green outline to provide clearer visibility into patient readiness. This feature can be enabled through screen configuration.',
      },
    ],
  },
  {
    version: '4.4.0',
    date: 'Sep 2026',
    headline: 'Analytics PDFs, new KPIs, and PreOp Board updates',
    summary:
      'This release adds new analytics PDFs and KPIs, expands physician and open-block reporting, and updates PreOp Board, tablet, and staffing workflows.',
    highlights: ['CPT, Specialty, and Operations analytics PDFs', 'Net Cancellation Rate', 'PreOp Board S-Ready alerts'],
    items: [
      {
        category: 'New',
        title: 'Net Cancellation Rate',
        audience: 'Administrators',
        description:
          'Analytics now includes Net Cancellation Rate, calculated as (cancelled cases − rescheduled cases) ÷ total scheduled cases × 100.',
      },
      {
        category: 'New',
        title: 'PreOp to Ready for OR KPI',
        audience: 'Administrators',
        description: 'A new KPI measures the time from when a patient enters PreOp until they are ready for the OR.',
      },
      {
        category: 'New',
        title: 'New Analytics PDFs',
        audience: 'Administrators',
        description:
          'New analytics PDFs are available for download: CPT Analytics, Specialty Analytics, and Operations Analytics.',
      },
      {
        category: 'New',
        title: 'CPT Code Analysis',
        audience: 'Administrators',
        description: 'CPT code analysis is now available in analytics.',
      },
      {
        category: 'New',
        title: 'Physician OR Utilization',
        audience: 'Administrators',
        description: 'Analysis by Physician now includes physician OR utilization.',
      },
      {
        category: 'New',
        title: 'Open Block Analytics',
        audience: 'Administrators, Schedulers',
        description:
          'Open block scheduling logic has been updated, and new Open Block analytics show how open block time is scheduled and used.',
      },
      {
        category: 'New',
        title: 'Staff Analytics Time Filter',
        audience: 'Administrators',
        description: 'Staff analytics can now be filtered by time of day, such as 5 AM to 5 PM.',
      },
      {
        category: 'Improved',
        title: 'Hourly Rates with Cents',
        audience: 'Administrators',
        description: 'Staff hourly rates can now include cents, such as $28.30.',
      },
      {
        category: 'New',
        title: 'PreOp Tablet Sort by Physician',
        audience: 'Pre-Op Nurses',
        description: 'The PreOp tablet can now be configured to sort patients alphabetically by physician.',
      },
      {
        category: 'Improved',
        title: 'Anesthesiologist Selection on the PreOp Board',
        audience: 'Pre-Op Nurses',
        description: 'The anesthesiologist column on the PreOp Board is now a searchable dropdown of anesthesiologists.',
      },
      {
        category: 'New',
        title: 'S-Ready Chime on the Internal Screen',
        audience: 'OR Staff, Pre-Op Nurses',
        description: 'When a surgeon marks a patient as S-Ready, a chime plays on the Internal Screen.',
      },
      {
        category: 'New',
        title: 'S-Ready Staff SMS',
        audience: 'Staff',
        description: 'When a surgeon marks a patient as S-Ready, an SMS can be sent to a selected staff member.',
      },
      {
        category: 'New',
        title: 'OR Delay Reasons on the PreOp iPad',
        audience: 'Pre-Op Nurses',
        description: 'OR delay reasons can now be recorded from the PreOp iPad.',
      },
    ],
  },
  {
    version: '4.3.2',
    date: 'Sep 2026',
    headline: 'Clearer OR readiness KPI names',
    summary:
      'This release renames OR readiness KPIs so they more closely match the work they represent and reduce confusion in reporting.',
    highlights: ['Cleaning Complete KPI', 'OR Ready for Next Patient KPI'],
    internalHighlights: ['Installation acceptance module'],
    items: [
      {
        category: 'Improved',
        title: 'KPI Name Update: OR Readiness',
        audience: 'Administrators',
        description:
          'Two KPIs were renamed to prevent confusion: OR Room Ready is now Cleaning Complete, and OR Room Ready Next Patient is now OR Ready for Next Patient.',
      },
      {
        category: 'New',
        title: 'Installation Acceptance Module',
        audience: 'Internal Staff',
        internal: true,
        description:
          'A new installation acceptance module improves the post-installation process. Uploaded photos are loaded onto the platform automatically, and an Acceptance PDF can be generated directly from the platform.',
      },
    ],
  },
  {
    version: '4.3.1',
    date: 'Aug 2026',
    headline: 'Internal screen details, SMS reminders, return-patient workflow, and PACU analytics',
    summary:
      'This release adds richer patient details and sorting on internal screens, new physician and patient SMS reminders, a return-patient workflow, and improvements to analytics including PACU charts.',
    highlights: [
      'Patient details on internal screens',
      'First-case and arrival SMS reminders',
      'PACU analytics chart improvements',
    ],
    items: [
      {
        category: 'New',
        title: 'Patient Identifier on Internal Screen',
        audience: 'All Users',
        description:
          'Age, gender, and OR information are now shown on the Scheduled Patients column and Internal Screen tiles, so staff have this information before and during a patient’s visit.',
      },
      {
        category: 'New',
        title: 'Scheduled Patients Carousel Speed',
        audience: 'All Users',
        description: 'The rotating Scheduled Patients carousel on the Internal Screen can now have its speed adjusted.',
      },
      {
        category: 'New',
        title: 'Patient Sorting by Scheduled Procedure Time',
        audience: 'All Staff',
        description:
          'Patients on the Internal Screen and PreOp / PACU iPads can now be sorted by scheduled procedure time.',
      },
      {
        category: 'New',
        title: 'Physician SMS — First Case Start Time',
        audience: 'Physicians',
        description:
          'Physicians can now receive an SMS notification for their first case’s scheduled start time. This SMS can be configured to send a set number of hours before the procedure.',
      },
      {
        category: 'New',
        title: 'Patient SMS — Arrival Time Reminder',
        audience: 'Patients',
        description:
          'Patients can now receive an SMS reminder of when to arrive for surgery. This SMS can be configured to send at a specific time on a specific day prior to surgery.',
      },
      {
        category: 'Improved',
        title: 'Procedure Step Color Coding',
        audience: 'All Users',
        description: 'Color coding for procedure steps can now be updated without a software deployment.',
      },
      {
        category: 'New',
        title: 'Return Patient Workflow',
        audience: 'Inpatient, PACU Nurses',
        description:
          'Inpatients returning for surgery can keep their bracelet after discharge and automatically register when they come back, going directly to PreOp or OR without a secondary check-in. PACU nurses can discharge while keeping the bracelet for returning inpatients.',
      },
      {
        category: 'Improved',
        title: 'Analytics Landing Page',
        audience: 'All Users',
        description: 'The Analytics landing page now opens on the General tab.',
      },
      {
        category: 'New',
        title: 'OR iPad — Patient Not Detected',
        audience: 'OR Staff',
        description:
          'An option is now available to disable the “Patient not detected” button on the OR iPad to prevent accidental clicks.',
      },
      {
        category: 'Improved',
        title: 'PACU Analytics Charts',
        audience: 'Administrators',
        description:
          'PACU analytics charts have been improved, including clearer journey visualization and milestone charts for PACU In to Chair, PT Start, and PT Complete.',
      },
    ],
  },
  {
    version: '4.3.0',
    date: 'Jul 2026',
    headline: 'Waiting room arrivals, nurse assignment, physician filters, and workflow persistence',
    summary:
      'This release improves waiting room and PreOp visibility, adds configurable nurse assignment and physician filtering on internal screens, and strengthens analytics and patient journey workflows.',
    highlights: [
      'Arrived patients on WR / PreOp screens',
      'Nurse assignment and physician filters',
      'PreOp notes and caretaker info persistence',
    ],
    items: [
      {
        category: 'New',
        title: 'Arrived Patients on WR / PreOp Internal Screen',
        audience: 'Pre-Op Nurses, Front Desk Staff',
        description:
          'Arrived patients can now appear on the Waiting Room tile of the WR / PreOp internal screen, visually differentiated from registered patients. This feature is configurable and requires a WR tablet.',
      },
      {
        category: 'New',
        title: 'Nurse Assignment in PreOp and PACU iPad',
        audience: 'Pre-Op Nurses, PACU Nurses',
        description:
          'Nurses can now be assigned to patients from the PreOp and PACU iPad workflows. This feature is configurable via PreOp and PACU iPad settings.',
      },
      {
        category: 'New',
        title: 'Physician Filter for OR/PR Tile Visibility',
        audience: 'Physicians, OR Staff',
        description:
          'Physicians in OR / PR can now filter the OR TV to show only their own patients. This feature is configurable via internal screen settings.',
      },
      {
        category: 'New',
        title: 'Custom Date Range for Analytics',
        audience: 'Administrators',
        description:
          'Analytics now supports custom date ranges, giving greater flexibility when reviewing analytics data.',
      },
      {
        category: 'Improved',
        title: 'PreOp Notes Persistence',
        audience: 'OR Managers, Schedulers, Nurses',
        description:
          'PreOp notes entered from the Schedule screen now remain persistent as the patient moves into OR / PR.',
      },
      {
        category: 'Improved',
        title: 'Front Desk / Caretaker Info',
        audience: 'Front Desk Staff',
        description:
          'Caretaker phone numbers entered on a WR tablet are now automatically pushed to the Front Desk Dashboard.',
      },
    ],
  },
  {
    version: '4.2.0',
    date: 'Jul 2026',
    headline: 'Release notes, staff attribution, exports, reviews, and workflow updates',
    summary:
      'This release adds a dedicated Release Notes section, expands staff attribution and data export capabilities, and improves patient, nursing, and scheduling workflows.',
    highlights: ['Release Notes module', 'Staff attribution and exports', 'Nursing and scheduling workflows'],
    demoUrl: 'https://app.storylane.io/demo/pvacbqxdr8r2?embed=inline',
    demoAspectRatio: 50.95,
    items: [
      {
        category: 'New',
        title: 'Release Notes Module',
        audience: 'All Users',
        description:
          'A dedicated Release Notes section is now available within the About tab, making it easier to view the latest platform updates and enhancements.',
      },
      {
        category: 'New',
        title: 'Staff Attribution',
        audience: 'Administrators',
        description:
          'Staff costing attribution is now added upon procedure completion, enabling same-day case costing analysis and more accurate operational reporting.',
      },
      {
        category: 'New',
        title: 'Data Export Module',
        audience: 'Administrators',
        description:
          'Predefined analytics and Staff Attribution data can now be exported into CSV and Excel formats for further reporting and analysis.',
      },
      {
        category: 'New',
        title: 'Physician Review SMS',
        audience: 'Physicians',
        description:
          'Patients who submit a 4- or 5-star Ospitek review can now be redirected to their physician’s Google Review page, helping increase provider-specific online reviews.',
      },
      {
        category: 'New',
        title: 'Waiting Room Notes',
        audience: 'Front Desk Staff, Pre-Op Nurses',
        description:
          'Front desk notes can now be displayed on the Internal Screen for patients in the Waiting Room, allowing clinical staff to review important patient information before starting the PreOP process.',
      },
      {
        category: 'New',
        title: 'New KPI: Wheels Out to Next Patient Nerve Block',
        audience: 'Administrators, Nurses',
        description:
          'A new operational KPI measures the time between one patient leaving the OR and the next patient’s nerve block, providing greater insight into perioperative workflow efficiency.',
      },
      {
        category: 'New',
        title: 'PACU iPad View',
        audience: 'Pre-Op Nurses, PACU Nurses',
        description:
          'The PACU iPad workflow is now available to nurse user accounts, allowing Pre-Op and PACU nurses to perform all required actions directly from their computer without interacting with the iPad.',
      },
      {
        category: 'New',
        title: 'Room Change Override',
        audience: 'OR Managers, Schedulers',
        description:
          'Room changes made within Ospitek can now be permanently retained, preventing subsequent EMR updates from overwriting them.',
      },
      {
        category: 'New',
        title: 'Registration Package Default View',
        audience: 'Front Desk Staff',
        description:
          'Today’s Procedures can now be set as the default Registration Package view, replacing the previous Next 7 Days default for quicker access to the current day’s schedule.',
      },
    ],
  },
];
